import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface ThinkingStep {
  step: string;
  name: string;
  desc: string;
  icon: string;
  color: string;
}

interface AnalogyVsFirst {
  label: string;
  analogy: string;
  firstPrinciple: string;
}

interface CaseStudy {
  person: string;
  field: string;
  problem: string;
  insight: string;
  color: string;
}

interface MentalModel {
  name: string;
  icon: string;
  desc: string;
  color: string;
}

interface FirstPrinciplesTemplateProps {
  topic: string;
  subtitle: string;
  steps: ThinkingStep[];
  comparisons: AnalogyVsFirst[];
  cases: CaseStudy[];
  models: MentalModel[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: FirstPrinciplesTemplateProps = {
  topic: '第一性原理思维',
  subtitle: 'First Principles Thinking',
  steps: [
    { step: '01', name: '识别假设', desc: '列出你对问题的所有既有假设', icon: '🔍', color: '#ef4444' },
    { step: '02', name: '分解本质', desc: '将问题拆解到最基本的事实', icon: '🧩', color: '#f59e0b' },
    { step: '03', name: '验证基础', desc: '逐一验证每个基本事实是否成立', icon: '✅', color: '#22c55e' },
    { step: '04', name: '从零重构', desc: '基于验证后的事实重新构建方案', icon: '🏗️', color: '#3b82f6' },
  ],
  comparisons: [
    { label: '定价策略', analogy: '竞品卖100，我们卖95', firstPrinciple: '成本20+利润30=定价50' },
    { label: '火箭制造', analogy: '火箭就是要花6500万美元', firstPrinciple: '原材料成本仅占2%' },
    { label: '学习方法', analogy: '别人刷题我也刷题', firstPrinciple: '理解底层原理，举一反三' },
  ],
  cases: [
    {
      person: '埃隆·马斯克',
      field: 'SpaceX',
      problem: '火箭太贵无法商业化',
      insight: '自研火箭，复用助推器，成本降低90%',
      color: '#3b82f6',
    },
    {
      person: '查理·芒格',
      field: '投资决策',
      problem: '市场充满噪声难以判断',
      insight: '用多元思维模型交叉验证基本面',
      color: '#22c55e',
    },
    {
      person: '詹姆斯·戴森',
      field: '吸尘器',
      problem: '传统吸尘器吸力递减',
      insight: '从气旋分离原理出发，发明无袋吸尘器',
      color: '#f59e0b',
    },
  ],
  models: [
    { name: '5个为什么', icon: '❓', desc: '连续追问Why直到根因', color: '#ef4444' },
    { name: '苏格拉底式提问', icon: '🏛️', desc: '通过提问挑战每个前提', color: '#8b5cf6' },
    { name: '逆向工程', icon: '🔧', desc: '从结果倒推基本组件', color: '#06b6d4' },
    { name: '奥卡姆剃刀', icon: '✂️', desc: '最简单的解释往往是正确的', color: '#22c55e' },
  ],
  stats: [
    { value: '2300年', label: '源自亚里士多德的哲学方法' },
    { value: '90%↓', label: 'SpaceX 火箭成本降幅' },
    { value: '核心能力', label: '顶级创业者的共同思维' },
  ],
  bottomQuote: '不要让别人的思考结果替代了你自己的思考——回到事物的本质，从那里开始重建',
};

export const FirstPrinciplesTemplate: React.FC<
  Partial<FirstPrinciplesTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const stepStart = 60;
  const compareStart = 180;
  const caseStart = 300;
  const modelStart = 200;
  const statsStart = 440;
  const quoteStart = 490;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const treeDraw = interpolate(frame, [40, 140], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(160deg, #080812 0%, #0e1020 50%, #0a0c18 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: 35 }).map((_, i) => {
        const x = (i * 239) % 1920;
        const y = (i * 193) % 1080;
        const size = 1.5 + (i % 3);
        return (
          <div
            key={`dot-${i}`}
            style={{
              position: 'absolute',
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: '50%',
              background: '#22c55e',
              opacity: 0.06,
            }}
          />
        );
      })}

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 18}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#0a2010',
            borderRadius: 8,
            fontSize: 20,
            fontWeight: 700,
            color: '#4ade80',
            flexShrink: 0,
            border: '1px solid #4ade8030',
          }}
        >
          🧠 思维启发
        </div>
        <h1 style={{ fontSize: 48, fontWeight: 900, margin: 0, color: '#f1f5f9' }}>
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT: Decomposition Tree SVG ===== */}
      <svg
        style={{ position: 'absolute', left: 30, top: 110 }}
        width="620"
        height="452"
        viewBox="0 0 480 350"
      >
        <text x="240" y="20" fill="#64748b" fontSize="20" textAnchor="middle" fontWeight="600">
          问题分解树 · Decomposition Tree
        </text>

        {/* Root */}
        <rect
          x="170" y="40" width="140" height="40" rx="8"
          fill="#22c55e18" stroke="#22c55e" strokeWidth="2"
          opacity={interpolate(treeDraw, [0, 0.15], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        />
        <text x="240" y="65" fill="#22c55e" fontSize="19" textAnchor="middle" fontWeight="700"
          opacity={interpolate(treeDraw, [0, 0.15], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >复杂问题</text>

        {/* Level 1 branches */}
        {[{ x: 80, label: '假设 A' }, { x: 240, label: '假设 B' }, { x: 400, label: '假设 C' }].map((node, i) => {
          const branchOpacity = interpolate(treeDraw, [0.15 + i * 0.08, 0.3 + i * 0.08], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`l1-${i}`} opacity={branchOpacity}>
              <line x1="240" y1="80" x2={node.x} y2="120" stroke="#64748b" strokeWidth="1.5" strokeDasharray="4 3" />
              <rect x={node.x - 60} y="120" width="120" height="36" rx="6"
                fill="#1e293b" stroke="#475569" strokeWidth="1.5" />
              <text x={node.x} y="143" fill="#94a3b8" fontSize="17" textAnchor="middle" fontWeight="600">
                {node.label}
              </text>
            </g>
          );
        })}

        {/* Level 2 - Fundamental facts */}
        {[
          { x: 50, parent: 80, label: '事实 1', color: '#22c55e' },
          { x: 110, parent: 80, label: '事实 2', color: '#22c55e' },
          { x: 210, parent: 240, label: '事实 3', color: '#22c55e' },
          { x: 270, parent: 240, label: '事实 4', color: '#22c55e' },
          { x: 370, parent: 400, label: '事实 5', color: '#22c55e' },
          { x: 430, parent: 400, label: '事实 6', color: '#22c55e' },
        ].map((node, i) => {
          const factOpacity = interpolate(treeDraw, [0.45 + i * 0.06, 0.6 + i * 0.06], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={`l2-${i}`} opacity={factOpacity}>
              <line x1={node.parent} y1="156" x2={node.x} y2="190" stroke="#22c55e40" strokeWidth="1.5" />
              <rect x={node.x - 40} y="190" width="80" height="30" rx="6"
                fill="#22c55e15" stroke="#22c55e60" strokeWidth="1.5" />
              <text x={node.x} y="210" fill="#4ade80" fontSize="16" textAnchor="middle" fontWeight="600">
                {node.label}
              </text>
            </g>
          );
        })}

        {/* Rebuild arrow */}
        <path
          d="M240,235 L240,275"
          stroke="#f59e0b"
          strokeWidth="2"
          strokeDasharray="6 4"
          opacity={interpolate(treeDraw, [0.8, 1], [0, 0.8], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        />
        <polygon
          points="234,270 240,285 246,270"
          fill="#f59e0b"
          opacity={interpolate(treeDraw, [0.85, 1], [0, 0.8], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        />
        <rect
          x="160" y="288" width="160" height="40" rx="8"
          fill="#f59e0b18" stroke="#f59e0b" strokeWidth="2"
          opacity={interpolate(treeDraw, [0.9, 1], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        />
        <text x="240" y="313" fill="#f59e0b" fontSize="19" textAnchor="middle" fontWeight="700"
          opacity={interpolate(treeDraw, [0.9, 1], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
        >全新方案 ✨</text>
      </svg>

      {/* ===== RIGHT OF TREE: Analogy vs First Principles ===== */}
      <div
        style={{
          position: 'absolute',
          top: 110,
          left: 668,
          width: 712,
        }}
      >
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          类比思维 vs 第一性原理
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          {p.comparisons.map((c, i) => {
            const s = spring({
              frame: frame - (compareStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: '#0f0f23',
                  borderRadius: 14,
                  padding: '16px 14px',
                  border: '1px solid #1e293b',
                  opacity: s,
                  transform: `translateY(${(1 - s) * 20}px)`,
                }}
              >
                <div style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', marginBottom: 10 }}>{c.label}</div>
                <div style={{
                  background: '#ef444418',
                  borderRadius: 8,
                  padding: '8px 10px',
                  marginBottom: 8,
                  borderLeft: '3px solid #ef4444',
                }}>
                  <div style={{ fontSize: 20, color: '#ef4444', fontWeight: 600 }}>类比思维</div>
                  <div style={{ fontSize: 20, color: '#fca5a5', marginTop: 2 }}>{c.analogy}</div>
                </div>
                <div style={{
                  background: '#22c55e18',
                  borderRadius: 8,
                  padding: '8px 10px',
                  borderLeft: '3px solid #22c55e',
                }}>
                  <div style={{ fontSize: 20, color: '#22c55e', fontWeight: 600 }}>第一性原理</div>
                  <div style={{ fontSize: 20, color: '#86efac', marginTop: 2 }}>{c.firstPrinciple}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== RIGHT TOP: 4-Step Process ===== */}
      <div style={{ position: 'absolute', top: 120, right: 40, width: 480 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          四步实践法
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {p.steps.map((step, i) => {
            const s = spring({
              frame: frame - (stepStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(50% - 5px)',
                  background: '#0f0f23',
                  borderRadius: 14,
                  padding: '14px 14px',
                  border: `1.5px solid ${step.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 28 }}>{step.icon}</span>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 22, color: '#64748b', fontWeight: 700 }}>{step.step}</span>
                    <span style={{ fontSize: 20, fontWeight: 800, color: step.color }}>{step.name}</span>
                  </div>
                </div>
                <div style={{ fontSize: 18, color: '#94a3b8', lineHeight: 1.5 }}>{step.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== RIGHT BOTTOM: Mental Models ===== */}
      <div style={{ position: 'absolute', top: 470, right: 40, width: 480 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          辅助思维工具
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {p.models.map((model, i) => {
            const s = spring({
              frame: frame - (modelStart + i * 14),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: '#0f0f23',
                  borderRadius: 12,
                  padding: '14px 10px',
                  textAlign: 'center',
                  border: `1px solid ${model.color}30`,
                  opacity: s,
                  transform: `scale(${0.8 + 0.2 * s})`,
                }}
              >
                <div style={{ fontSize: 30, marginBottom: 4 }}>{model.icon}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: model.color }}>{model.name}</div>
                <div style={{ fontSize: 18, color: '#94a3b8', marginTop: 4, lineHeight: 1.4 }}>{model.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Case Studies ===== */}
      <div style={{ position: 'absolute', top: 720, left: 40, right: 40 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 12, letterSpacing: '0.08em', fontWeight: 600 }}>
          经典案例
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {p.cases.map((c, i) => {
            const s = spring({
              frame: frame - (caseStart + i * 20),
              fps,
              config: { damping: 14, stiffness: 110 },
            });
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  background: '#0f0f23',
                  borderRadius: 14,
                  padding: '16px 18px',
                  borderTop: `3px solid ${c.color}`,
                  opacity: s,
                  transform: `translateY(${(1 - s) * 24}px)`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 22, fontWeight: 900, color: c.color }}>{c.person}</span>
                  <span style={{ fontSize: 22, color: '#64748b', fontWeight: 600 }}>{c.field}</span>
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginBottom: 6 }}>
                  ❌ {c.problem}
                </div>
                <div style={{ fontSize: 20, color: '#4ade80', lineHeight: 1.5 }}>
                  ✅ {c.insight}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 90,
          left: 40,
          right: 40,
          display: 'flex',
          gap: 16,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#0f0f23',
                borderRadius: 12,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #1e293b',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  flexWrap: 'nowrap',
                }}
              >
                <span style={{ fontSize: 32, fontWeight: 900, color: '#4ade80', flexShrink: 0 }}>
                  {st.value}
                </span>
                <span style={{ fontSize: 16, color: '#64748b', lineHeight: 1.3, textAlign: 'left' }}>
                  {st.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 10,
          left: 40,
          right: 40,
          background: '#0f0f23',
          borderRadius: 14,
          padding: '14px 20px',
          borderTop: '3px solid #4ade80',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🧠</span>
          <span style={{ fontSize: 19, color: '#e2e8f0', lineHeight: 1.5 }}>{p.bottomQuote}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #ef4444, #f59e0b, #22c55e, #3b82f6)',
        }}
      />
    </AbsoluteFill>
  );
};
